import React, { useMemo, useState } from 'react';
import { BubbleBadge, BubbleButton, BubbleTable } from '../BubbleUI';
import { mapBy, notFalsy, notUndefined } from '@go-shared/lib/functional';
import { BubbleTableFields, BubbleTableTemplate } from '../BubbleUI/BubbleTable';
import {
  isHiddenVenueDueToBasicFilters,
  evaluateMcmEndpoint,
  evaluateMcmTruthyAggregate,
  toggleMcmEndpoint,
} from '@go-shared/lib/mcm';
import { mcmEndpointToIcon } from '@go-client/lib/mcm';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { Plan, VenueDisposition } from '@go-shared/types/plan';
import { HourFormat } from '@go-shared/types/brand';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { DailySchedulePanelMode } from './DailySchedulePanel';
import { CommonTextTranslation, VenueTemplateTranslation } from '@go-shared/types/translation';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import Fuse from 'fuse.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { findIconDefinition } from '@fortawesome/fontawesome-svg-core';
import { DEFAULT_TIMESTR_FORMAT, timeToMinutes } from '@go-shared/lib/time';
import {
  getBasicTimeStrings,
  getVenueAvailabilityStrings,
  getVenueDisplay,
  getVenueDisplayName,
  makeEmptyVenueDisposition,
} from '@go-shared/lib/venue';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import VenuesGlance from '../VenuesGlance';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
import { getCommonTexts } from '@go-shared/lib/brand';
import { evaluateAudienceTruthyAggregate, evaluateAudienceZone } from '@go-shared/lib/audience';

interface VenueTableItem {
  id: string;
  templateId: string;
  name: string;
  location: string;
  timeStrings: string[];
  order: number;
  _data: {
    venue: VenueDisposition;
    venueTemplate: VenueTemplate;
  };
}

interface DailyVenueListPanelPropsBase {
  mode: DailySchedulePanelMode;
  locked: boolean;
  search: string;
  focusEdp: string;
  focusLang: string | undefined;
  focusZone: string | undefined;
  mcmVisibleEdps: string[];
  plan: Plan;
  hourFormat: HourFormat;
  venueTemplates: VenueTemplate[];
  venueTemplateTranslations: VenueTemplateTranslation[];
  commonTextTranslations: CommonTextTranslation[];
  onUpdateVenue: (venue: VenueDisposition) => void;
}

type DailyVenueListPanelProps = DailyVenueListPanelPropsBase & React.HTMLAttributes<HTMLDivElement>;

function DailyVenueListPanel(props: DailyVenueListPanelProps) {
  const {
    mode,
    locked,
    search,
    focusEdp,
    focusLang,
    focusZone,
    mcmVisibleEdps,
    plan,
    hourFormat,
    venueTemplates,
    venueTemplateTranslations,
    onUpdateVenue,
    commonTextTranslations,
    ...rest
  } = props;

  const serverInfo = useServerInfo();
  const { activeProperty: property } = useActiveProperty();

  const [expandedReqId, setExpandedReqId] = useState<string | undefined>(undefined);

  const venueTemplateTranslationsMap = mapBy(venueTemplateTranslations, 'refers');

  function getItemStatus(item: VenueTableItem) {
    const focusVisible = evaluateMcmEndpoint(
      'visible',
      focusEdp,
      item._data.venue.mcm,
      item._data.venueTemplate.mcm
    );

    const focusTrending = evaluateMcmEndpoint(
      'trending',
      focusEdp,
      item._data.venue.mcm,
      item._data.venueTemplate.mcm
    );

    const zoneVisible = focusZone
      ? evaluateAudienceZone(
          'visible',
          focusZone,
          item._data.venue.audience,
          item._data.venueTemplate.audience
        )
      : true;

    const allVisible =
      evaluateMcmTruthyAggregate(
        'visible',
        mode === 'mcm' ? mcmVisibleEdps : serverInfo.mcmEndpoints,
        item._data.venue.mcm,
        item._data.venueTemplate.mcm
      ) &&
      evaluateAudienceTruthyAggregate(
        'visible',
        property.zones.map(z => z._id),
        item._data.venue.audience,
        item._data.venueTemplate.audience
      );

    const allTrending = evaluateMcmTruthyAggregate(
      'trending',
      mode === 'mcm' ? mcmVisibleEdps : serverInfo.mcmEndpoints,
      item._data.venue.mcm,
      item._data.venueTemplate.mcm
    );

    const translActive = focusLang
      ? ((venueTemplateTranslationsMap[item._data.venueTemplate._id]?.transforms ?? {})[focusLang]
          ?.active ?? false)
      : true;

    const typesActive =
      item._data.venueTemplate.types.filter(vtt => !vtt.hideFromSignage).length > 0;

    const visible = mode === 'focus' ? focusVisible && zoneVisible && typesActive : allVisible;
    const trending = mode === 'focus' ? focusTrending : allTrending;

    const hiddenFromBasicFilters = isHiddenVenueDueToBasicFilters(item._data.venueTemplate);

    return {
      visible: visible !== false && !hiddenFromBasicFilters,
      hiddenFromBasicFilters,
      expanded: item.templateId === expandedReqId,
      trending,
      translActive,
      typesActive,
    };
  }

  const venueDataMap = mapBy(plan.venueData, 'template');
  const dayStart = plan.dayStart;
  const dayStartMinutes = timeToMinutes(dayStart);
  const levelLabel = getPropertyConfiguration(property.type, 'levelLabel');

  const venueDatas = useMemo(
    () =>
      venueTemplates
        .filter(vt => !vt.archived)
        .map(vt => ({
          vt,
          vd: venueDataMap[vt._id] || makeEmptyVenueDisposition(vt._id),
        })),
    [venueTemplates, venueDataMap]
  );

  const tableItems: VenueTableItem[] = useMemo(
    () =>
      venueDatas
        .map(({ vt, vd }) => {
          const commonTexts = getCommonTexts(
            {
              default: serverInfo.brand.commonTexts,
              translations: commonTextTranslations,
            },
            { selected: focusLang }
          );
          let availabilities =
            mode === 'focus'
              ? getVenueAvailabilityStrings(
                  vd.hours,
                  dayStartMinutes,
                  hourFormat,
                  DEFAULT_TIMESTR_FORMAT,
                  commonTexts,
                  vd.displayOptions?.timeDisplay
                )
              : getBasicTimeStrings(vd.hours, dayStartMinutes, hourFormat);
          if (availabilities.length < 1) {
            availabilities.push('--');
          }

          const venueDisplay = getVenueDisplay(vd, vt, commonTexts, 'full');

          const result: VenueTableItem = {
            id: vd._id,
            templateId: vd.template,
            name: mode === 'focus' ? venueDisplay.name : vt.name,
            location:
              mode === 'focus'
                ? venueDisplay.location
                : `${levelLabel} ${getVenueDisplayName(vt, commonTexts)}`,
            timeStrings: availabilities,
            order: vt.order,
            _data: {
              venue: vd,
              venueTemplate: vt,
            },
          };

          return result;
        })
        .filter(notUndefined)
        .sort((v1, v2) => v1.order - v2.order),
    [venueDatas]
  );

  const fuse = useMemo(() => {
    return new Fuse(tableItems, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });
  }, [tableItems]);

  const fuseSearchResults = useMemo(() => {
    if (!search || !fuse) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const filteredTableItems = fuseSearchResults || tableItems;

  const cntFields: Array<BubbleTableFields<VenueTableItem>> = [
    {
      key: 'title',
      tdProps: { className: 'w-full' },
    },
    {
      key: 'time',
    },
    {
      key: 'actions-1',
      label: '',
      tdProps: { className: 'whitespace-nowrap p-1-i text-right' },
    },
    {
      key: 'actions-2',
      label: '',
      tdProps: { className: 'whitespace-nowrap p-1-i text-right' },
    },
    {
      key: 'minimizer',
      label: '',
      tdProps: { className: 'whitespace-nowrap p-1-i pr-3-i text-right' },
    },
  ];

  function makeDefaultTemplateTdProps(
    data: VenueTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const { visible, expanded } = getItemStatus(data);

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        expanded ? 'bg-bubblegum-lightest' : undefined,
        !visible ? 'italic text-slate-lighter' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
    };
  }

  const cntTemplates: Array<BubbleTableTemplate<VenueTableItem>> = [
    {
      key: 'time',
      tdProps: makeDefaultTemplateTdProps,
      render: item => {
        return (
          <>
            {item.timeStrings.map((ts, i) => (
              <div key={`${ts}_${i}`} className="whitespace-nowrap">
                {ts}
              </div>
            ))}
          </>
        );
      },
    },
    {
      key: 'title',
      tdProps: makeDefaultTemplateTdProps,
      render: item => {
        return (
          <>
            <div className="font-semibold">{item.name}</div>
          </>
        );
      },
    },
    {
      key: 'actions-1',
      tdProps: (item, props) => {
        const result = makeDefaultTemplateTdProps(item, props);
        if (mode === 'default' || mode === 'mcm') {
          result.className += ' hidden';
        }
        return result;
      },
      render: item => {
        const { translActive, typesActive } = getItemStatus(item);

        let statuses = <></>;

        if (mode === 'focus') {
          statuses = (
            <>
              <BubbleBadge variant={typesActive ? 'smoke' : 'crimson'} className="mt-1 mr-1">
                <FontAwesomeIcon
                  fixedWidth
                  icon={
                    typesActive
                      ? 'tag'
                      : findIconDefinition({ prefix: 'fak', iconName: 'solid-tag-slash' })
                  }
                />
              </BubbleBadge>
              <BubbleBadge variant={translActive ? 'smoke' : 'crimson'} className="mt-1 mr-1">
                <FontAwesomeIcon
                  fixedWidth
                  icon={
                    translActive
                      ? 'language'
                      : findIconDefinition({ prefix: 'fak', iconName: 'solid-language-slash' })
                  }
                />
              </BubbleBadge>
            </>
          );
        }

        return <>{statuses}</>;
      },
    },
    {
      key: 'actions-2',
      tdProps: makeDefaultTemplateTdProps,
      render: item => {
        const { visible, trending, hiddenFromBasicFilters } = getItemStatus(item);

        let edpActions = <></>;

        if (mode === 'default') {
          edpActions = (
            <>
              <BubbleButton
                className="p-2-i"
                variant={visible === null ? 'banana' : visible ? 'navy' : 'smoke'}
                inverse
                icon={visible === null ? 'eye' : visible ? 'eye' : 'eye-slash'}
                onClick={() => {
                  const vd = {
                    ...item._data.venue,
                    mcm: toggleMcmEndpoint(
                      'visible',
                      'all',
                      serverInfo.mcmEndpoints,
                      item._data.venue.mcm,
                      item._data.venueTemplate.mcm
                    ),
                  };
                  onUpdateVenue(vd);
                }}
              />
              <BubbleButton
                className="p-2-i"
                variant={trending === null ? 'banana' : trending ? 'crimson' : 'smoke'}
                inverse
                icon="fire"
                onClick={() => {
                  const vd = {
                    ...item._data.venue,
                    mcm: toggleMcmEndpoint(
                      'trending',
                      'all',
                      serverInfo.mcmEndpoints,
                      item._data.venue.mcm,
                      item._data.venueTemplate.mcm
                    ),
                  };
                  onUpdateVenue(vd);
                }}
              />
            </>
          );
        } else if (mode === 'mcm') {
          /* LABS-4777: Always show hidden eye icon if basic filters would hide the content,
          regardless of the MCM status at the time*/
          edpActions = (
            <>
              {mcmVisibleEdps.map(edp => {
                const mcmVisible =
                  !hiddenFromBasicFilters &&
                  evaluateMcmEndpoint(
                    'visible',
                    edp,
                    item._data.venue.mcm,
                    item._data.venueTemplate.mcm
                  );

                return (
                  <BubbleButton
                    key={edp}
                    className="p-2-i"
                    variant={mcmVisible ? 'navy' : 'smoke'}
                    inverse
                    icon={mcmEndpointToIcon(edp, mcmVisible)}
                    onClick={e => {
                      e.stopPropagation();
                      const evt = {
                        ...item._data.venue,
                        mcm: toggleMcmEndpoint(
                          'visible',
                          edp,
                          serverInfo.mcmEndpoints,
                          item._data.venue.mcm,
                          item._data.venueTemplate.mcm
                        ),
                      };
                      onUpdateVenue(evt);
                    }}
                  />
                );
              })}
            </>
          );
        } else if (mode === 'focus') {
          const mcmVisible =
            !hiddenFromBasicFilters &&
            evaluateMcmEndpoint(
              'visible',
              focusEdp,
              item._data.venue.mcm,
              item._data.venueTemplate.mcm
            );

          edpActions = (
            <BubbleButton
              key={focusEdp}
              className="p-2-i"
              variant={mcmVisible ? 'navy' : 'smoke'}
              inverse
              icon={mcmEndpointToIcon(focusEdp, mcmVisible)}
              onClick={e => {
                e.stopPropagation();
                const cnt = {
                  ...item._data.venue,
                  mcm: toggleMcmEndpoint(
                    'visible',
                    focusEdp,
                    serverInfo.mcmEndpoints,
                    item._data.venue.mcm,
                    item._data.venueTemplate.mcm
                  ),
                };
                onUpdateVenue(cnt);
              }}
            />
          );
        }
        return <fieldset disabled={locked}>{edpActions}</fieldset>;
      },
    },
    {
      key: 'minimizer',
      tdProps: makeDefaultTemplateTdProps,
      render: item => {
        return (
          <BubbleButton
            className="p-2-i"
            variant="navy"
            inverse
            icon={
              expandedReqId === item.templateId
                ? 'arrow-down-left-and-arrow-up-right-to-center'
                : 'arrow-up-right-and-arrow-down-left-from-center'
            }
            onClick={() => {
              setExpandedReqId(expandedReqId === item.templateId ? undefined : item.templateId);
            }}
          />
        );
      },
    },
  ];

  function tableDetailsRender(item: VenueTableItem) {
    return (
      <div className="pb-3 pt-2">
        <div className="bg-white border shadow relative">
          <div className="px-4 py-3">
            <VenuesGlance
              locked={locked}
              venue={item._data.venue}
              onUpdateVenue={onUpdateVenue}
              plan={plan}
              template={item._data.venueTemplate}
              name={item.name}
              times={item.timeStrings}
            />
          </div>
        </div>
      </div>
    );
  }

  const venCountData = tableItems.map(getItemStatus);
  const visibleCount = venCountData.filter(ti => ti.visible === true).length;
  const notVisibleCount = venCountData.filter(ti => ti.visible === false).length;
  const partialVisibleCount = venCountData.filter(ti => ti.visible === null).length;

  return (
    <BubbleTable
      {...rest}
      responsive
      slim
      striped
      hideHeader
      className="shadow-none-i"
      keyExtractor={itm => itm.templateId}
      fields={cntFields}
      items={filteredTableItems}
      templates={cntTemplates}
      detailsKey={expandedReqId}
      detailsRender={tableDetailsRender}
      detailsClassName="bg-bubblegum-lightest"
      stickyHeader={37}
      showPretitle
      pretitleClassName="p-0-i"
      pretitleRender={() => (
        <div className="flex space-x-2 no-scrollbar items-center p-2 bg-eggshell border-b overflow-x-auto">
          <BubbleBadge variant="navy" className="flex-shrink-0">
            <FontAwesomeIcon icon="list" className="mr-1" />
            <span>{visibleCount + partialVisibleCount + notVisibleCount}</span>
          </BubbleBadge>
          <BubbleBadge variant="banana" className="flex-shrink-0">
            <FontAwesomeIcon icon="eye" className="mr-1" />
            <span>{visibleCount + partialVisibleCount}</span>
          </BubbleBadge>
          <BubbleBadge variant="smoke" className="flex-shrink-0">
            <FontAwesomeIcon icon="eye-slash" className="mr-1" />
            <span>{notVisibleCount}</span>
          </BubbleBadge>
          {mcmVisibleEdps
            .filter(edp => (mode === 'focus' ? focusEdp === edp : true))
            .map(edp => (
              <BubbleBadge key={edp} variant="navy" className="flex-shrink-0">
                <FontAwesomeIcon icon={mcmEndpointToIcon(edp, true)} className="mr-1" />
                <span>
                  {
                    tableItems
                      .map(ti =>
                        evaluateMcmEndpoint(
                          'visible',
                          edp,
                          ti._data.venue.mcm,
                          ti._data.venueTemplate.mcm
                        )
                      )
                      .filter(notFalsy).length
                  }
                </span>
              </BubbleBadge>
            ))}
        </div>
      )}
    />
  );
}

export default DailyVenueListPanel;
